import React, { useState, useRef } from 'react';
import { Button } from 'rebass';
import { FiPlusCircle } from 'react-icons/fi';
import { useToasts } from 'react-toast-notifications';
import classNames from 'classnames';

import helpers from 'utils/helpers';
import { useMergeTagOptions } from 'hooks/useMergeTagOptions';
import { PositionerV2 } from 'components/Positioner';
import NiceDropdown from 'components/NiceDropdown';
import Portal from 'components/Portal';

import styles from './styles.module.css';

/**
 * Displays a dropdown with personalised tokens (merge tags) that can be copied to the clipboard.
 *
 * @param {{ custom_sp_attributes: { title: string, id: string }[] }} [study] The study object.
 * @param {import('utils/constants').EMAIL_TEMPLATE_TYPE} [templateType] The template type.
 * @param {React.CSSProperties} [style] Additional styles.
 * @param {string} [className] Additional class names.
 * @param {boolean} [includeStudyOptions=true] Whether to include study-related merge tags.
 */
export function MergeTagsDropdown({ study, templateType, style, className, includeStudyOptions = true }) {
    const anchorRef = useRef();
    const { options, isLoading } = useMergeTagOptions({ study, templateType, includeStudyOptions });
    const [isOpen, setIsOpen] = useState(false);
    const { addToast } = useToasts();

    const copyMergeTag = value => {
        const mergeTag = `{${value}}`;
        helpers.copyToClipboard(mergeTag);
        addToast(`${mergeTag} copied to clipboard`, {
            appearance: 'success',
            autoDismiss: true
        });
        setIsOpen(false);
    };

    return (
        <>
            <Button
                variant="primary-transparent"
                disabled={isLoading}
                type="button"
                style={style}
                className={classNames(styles.anchorButton, className)}
                onClick={() => setIsOpen(true)}
                ref={anchorRef}
            >
                <FiPlusCircle />
                Merge tags
            </Button>
            <Portal isOpen={isOpen}>
                <PositionerV2 anchorRef={anchorRef}>
                    <NiceDropdown
                        style={{ inset: 'auto', position: 'static' }}
                        onClose={() => setIsOpen(false)}
                        showSearch
                        searchAutoFocus={false}
                        onChange={value => copyMergeTag(value)}
                        items={options.map(option => ({
                            id: option.value,
                            title: option.label,
                            label: option.disabled
                        }))}
                    />
                </PositionerV2>
            </Portal>
        </>
    );
}
